import { SingleDatePicker } from '@churchonline/calendar';
import React, { ChangeEvent, FC, FormEventHandler, useState } from 'react';

import { useRouter } from 'next/router';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import { useMutation, useQuery } from '@apollo/client';
import {
  CURRENT_ORGANIZATION_ONBOARDING,
  DELETE_SERVICE,
  LOAD_SERVICE,
  SAVE_ONBOARDING_STEP,
  SAVE_SERVICE,
  SERVICES_CONNECTION,
} from '@lib/queries';
import {
  formatTime,
  getDateTimeUTC,
  recurrenceType,
  validTime,
  serviceTimeIsUpcoming,
  serviceTimeHasPast,
} from '@components/Utils';
import { v4 as uuid } from 'uuid';
import {
  Box,
  FormBanner,
  FormBannerType,
  Grid,
  GridItem,
  Input,
  InputSize,
  LinkButton,
  LinkButtonType,
  Modal,
  ModalSize,
  OHeaderThree,
  OParagraph,
  OXSmallParagraph,
  PrimaryButton,
  SecondaryRiskButton,
  Select,
  ClosedLockIcon,
  useTimezoneData,
} from '@lifechurch/react-ion';
import { DateUtils } from 'react-day-picker';
import { DayModifiers } from 'react-day-picker';
import { AlertType, useAlertContext } from '@lib/alertContext';
import { useServicesContext } from '@lib/servicesContext';
import { useBeforeUnloadContext } from '@lib/beforeUnloadContext';
import {
  ApplyEnum,
  CurrentOrganizationOnboardingQuery,
  CurrentOrganizationOnboardingQueryVariables,
  DeleteServiceMutation,
  DeleteServiceMutationVariables,
  LoadServiceQuery,
  LoadServiceQueryVariables,
  RecurrenceEnum,
  SaveOnboardingStepMutation,
  SaveServiceMutation,
  SaveServiceMutationVariables,
  ServiceInput,
} from '@generated/types';
import { PageHeader } from '@components/PageHeader';
import BackButton from '@components/BackButton';
import Link from 'next/link';
import styled from 'styled-components';
import { OnboardingStepKeys } from '@components/Onboarding/ChecklistInProgress/ChecklistItems/ChecklistItem';
import { GetServerSideProps } from 'next';
import { DEFAULT_LOCALE } from '@utils/constants';
import { serverSideTranslations } from 'next-i18next/serverSideTranslations';
import { useTranslation } from 'next-i18next';
import { useLocalizedMonths } from '@lib/useLocalizedMonths';
import { Metrics } from '@lib/metrics';

enum ModalType {
  RECURRENCE_CHANGE = 'RECURRENCE_CHANGE',
  SAVE_REPEATING = 'SAVE_REPEATING',
  DELETE_REPEATING = 'DELETE_REPEATING',
  DEFAULT = '',
}

interface TimezoneData {
  value: string;
  display: string;
  utcOffset: number;
}

interface ServiceStateInterface {
  date?: Date;
  time: string;
  dateValid: boolean;
  timeValid: boolean;
  recurrence: RecurrenceEnum;
  dayOfMonth: number;
  dayOfWeek: string;
  showEndRepeat: number;
  endRepeat?: Date;
  fromDate: Date;
  modalOpen: boolean;
  modalType: ModalType;
}

const defaultState: ServiceStateInterface = {
  date: new Date(),
  time: '10:00am',
  dateValid: true,
  timeValid: true,
  recurrence: RecurrenceEnum.ONCE,
  dayOfMonth: 1,
  dayOfWeek: dayjs(new Date()).locale('en').format('dd').toUpperCase(),
  showEndRepeat: 0,
  endRepeat: undefined,
  fromDate: new Date(),
  modalOpen: false,
  modalType: ModalType.DEFAULT,
};

const BackButtonStyleHelper = styled.a`
  &:hover {
    text-decoration: none;
  }
`;

const TimeZoneParagraph = styled(OXSmallParagraph)`
  color: ${props => props.theme.colors.gray50};
  margin-top: 2px;
`;

const getInput = (id: string, state: ServiceStateInterface, date: Date): ServiceInput => {
  let dayOfMonth = undefined;
  let monthOfYear = undefined;
  if (state.recurrence === 'MONTHLY' && state.dayOfWeek === '') {
    dayOfMonth = state.dayOfMonth;
  } else if (state.recurrence === 'ONCE' || state.recurrence === 'YEARLY') {
    dayOfMonth = date.getDate();
    monthOfYear = date.getMonth() + 1;
  }
  return {
    id: id,
    scheduleTime: getDateTimeUTC(date, state.time),
    recurrence: state.recurrence,
    year: state.recurrence === 'ONCE' ? date.getFullYear() : undefined,
    monthOfYear: monthOfYear,
    dayOfMonth: dayOfMonth,
    dayOfWeek:
      state.recurrence === 'WEEKLY' || (state.recurrence === 'MONTHLY' && state.dayOfWeek !== '')
        ? state.dayOfWeek
        : undefined,
    weekOfMonth:
      state.recurrence === 'MONTHLY' && state.dayOfWeek !== '' && state.dayOfMonth < 5
        ? state.dayOfMonth
        : undefined,
    endRepeat: state.endRepeat ? getDateTimeUTC(state.endRepeat, state.time) : null,
  };
};

interface TZProps {
  tz: string;
}

export const TimeZone: FC<TZProps> = ({ tz }) => {
  const data = useTimezoneData();

  let tzObj: TimezoneData = {
    value: 'Etc/UTC',
    display: 'UTC',
    utcOffset: 0,
  };

  Object.keys(data).forEach(key => {
    const foundTz = data[key].find(obj => obj.value === tz);
    if (foundTz) {
      tzObj = foundTz;
    }
  });

  const minutes = Math.abs(tzObj.utcOffset % 60);
  const hour = Math.floor(tzObj.utcOffset / 60);
  const utcOffset = minutes > 0 ? `${hour}:${minutes}` : `${hour}:00`;
  const time = `(GMT${hour < 0 ? utcOffset : '+' + utcOffset}) ${tzObj.display}`;

  return <TimeZoneParagraph>{time}</TimeZoneParagraph>;
};

interface SelectProps {
  onChange(e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void;

  value: string | number;
  name: string;
  day?: boolean;
  disabled?: boolean;
}

interface ModalProps {
  dismiss: () => void;
  saveService: (applyTo: ApplyEnum) => void;
  deleteService: (applyTo: ApplyEnum) => void;
  open: boolean;
  type: ModalType;
  from?: string;
  to?: string;
}

const AlertModal: FC<ModalProps> = ({
  dismiss,
  deleteService,
  saveService,
  open,
  type,
  from,
  to,
}) => {
  const { t } = useTranslation(['service', 'common']);

  return (
    <Modal open={open} dismiss={dismiss} size={ModalSize.NARROW}>
      {type === ModalType.RECURRENCE_CHANGE && (
        <>
          <OHeaderThree marginBlockEnd={3}>{t('changing_repeating_service')}</OHeaderThree>
          <OParagraph marginBlockEnd={3}>
            {t('changing_from_to', { from, to: to || t('one_time') })}
          </OParagraph>
          <FormBanner type={FormBannerType.Warning} withIcon>
            {t('any_content_added_to_future_deleted')}
          </FormBanner>
          <Box
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'flex-end'}
            marginBlockStart={5}
          >
            <LinkButton onClick={dismiss} buttonType={LinkButtonType.SECONDARY}>
              {t('common:cancel')}
            </LinkButton>
            <LinkButton onClick={() => saveService(ApplyEnum.FUTURE)} marginInlineStart={5}>
              {t('update_services')}
            </LinkButton>
          </Box>
        </>
      )}
      {type === ModalType.SAVE_REPEATING && (
        <>
          <OHeaderThree marginBlockEnd={3}>{t('changing_repeating_service')}</OHeaderThree>
          <OParagraph>{t('affect_this_service_or_all')}</OParagraph>
          <Box
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'flex-end'}
            marginBlockStart={5}
          >
            <LinkButton onClick={() => saveService(ApplyEnum.FUTURE)}>
              {t('all_future_services')}
            </LinkButton>
            <LinkButton onClick={() => saveService(ApplyEnum.CURRENT)} marginInlineStart={5}>
              {t('only_this_service')}
            </LinkButton>
          </Box>
        </>
      )}
      {type === ModalType.DELETE_REPEATING && (
        <>
          <OHeaderThree marginBlockEnd={3}>{t('deleting_repeating_service')}</OHeaderThree>
          <OParagraph>{t('delete_this_service_or_all')}</OParagraph>
          <Box
            display={'flex'}
            flexDirection={'row'}
            justifyContent={'flex-end'}
            marginBlockStart={5}
          >
            <LinkButton
              onClick={() => deleteService(ApplyEnum.FUTURE)}
              buttonType={LinkButtonType.ERROR}
            >
              {t('all_future_services')}
            </LinkButton>
            <LinkButton
              onClick={() => deleteService(ApplyEnum.CURRENT)}
              buttonType={LinkButtonType.ERROR}
              marginInlineStart={5}
            >
              {t('only_this_service')}
            </LinkButton>
          </Box>
        </>
      )}
    </Modal>
  );
};

const DayOfWeekSelect: FC<SelectProps> = ({
  onChange,
  value,
  name,
  day = false,
  disabled = false,
}) => {
  const { t } = useTranslation(['service', 'common']);

  return (
    <GridItem alignSelf={'end'} gridColumn={['span 12', 'span 3', 'span 2']}>
      <Select
        name={name}
        onChange={onChange}
        value={value}
        disabled={disabled}
        data-testid='dayOfWeek'
      >
        {day && (
          <optgroup>
            <option value=''>{t('day')}</option>
          </optgroup>
        )}
        <optgroup>
          <option value='SU'>{t('sunday')}</option>
          <option value='MO'>{t('monday')}</option>
          <option value='TU'>{t('tuesday')}</option>
          <option value='WE'>{t('wednesday')}</option>
          <option value='TH'>{t('thursday')}</option>
          <option value='FR'>{t('friday')}</option>
          <option value='SA'>{t('saturday')}</option>
        </optgroup>
      </Select>
    </GridItem>
  );
};

const DayOfMonthSelect: FC<SelectProps> = ({ onChange, value, name }) => {
  const { t } = useTranslation(['service', 'common']);
  return (
    <GridItem alignSelf={'end'} gridColumn={['span 12', 'span 3', 'span 2']}>
      <Select name={name} onChange={onChange} value={value} data-testid='dayOfMonth'>
        <optgroup>
          <option value={31}>{t('last')}</option>
        </optgroup>
        <optgroup>
          <option value={1}>1st</option>
          <option value={2}>2nd</option>
          <option value={3}>3rd</option>
          <option value={4}>4th</option>
          <option value={5}>5th</option>
          <option value={6}>6th</option>
          <option value={7}>7th</option>
          <option value={8}>8th</option>
          <option value={9}>9th</option>
          <option value={10}>10th</option>
          <option value={11}>11th</option>
          <option value={12}>12th</option>
          <option value={13}>13th</option>
          <option value={14}>14th</option>
          <option value={15}>15th</option>
          <option value={16}>16th</option>
          <option value={17}>17th</option>
          <option value={18}>18th</option>
          <option value={19}>19th</option>
          <option value={20}>20th</option>
          <option value={21}>21st</option>
          <option value={22}>22nd</option>
          <option value={23}>23rd</option>
          <option value={24}>24th</option>
          <option value={25}>25th</option>
          <option value={26}>26th</option>
          <option value={27}>27th</option>
          <option value={28}>28th</option>
          <option value={29}>29th</option>
          <option value={30}>30th</option>
          <option value={31}>31st</option>
        </optgroup>
      </Select>
    </GridItem>
  );
};

const Service: React.FC = () => {
  const [state, setState] = useState<ServiceStateInterface>(defaultState);
  const [serviceInPast, setServiceInPast] = useState<boolean>(false);
  const { from, to } = useServicesContext();
  const { openAlert } = useAlertContext();
  const { t } = useTranslation(['service', 'common']);
  const months = useLocalizedMonths();

  const router = useRouter();
  const locale = router.locale;
  const { id = '' } = router.query;
  const { maybeRouteTo } = useBeforeUnloadContext();
  dayjs.extend(customParseFormat);

  const { data } = useQuery<LoadServiceQuery, LoadServiceQueryVariables>(LOAD_SERVICE, {
    variables: {
      id: id !== 'new' ? id.toString() : '',
    },
    fetchPolicy: 'network-only',
    onCompleted: data => {
      if (data && data.loadService) {
        const { loadService: service } = data;
        let dayOfMonth = 1;
        if (service.recurrence === 'MONTHLY' && service.weekOfMonth) {
          dayOfMonth = service.weekOfMonth;
        } else if (service.dayOfMonth) {
          dayOfMonth = service.dayOfMonth;
        }
        setState({
          ...state,
          date: new Date(service.scheduleTime),
          time: dayjs(service.scheduleTime).format('h:mma'),
          recurrence: service.recurrence,
          dayOfMonth: dayOfMonth,
          dayOfWeek: service.dayOfWeek ? service.dayOfWeek : '',
          endRepeat: service.endRepeat ? new Date(service.endRepeat) : undefined,
          showEndRepeat: service.endRepeat ? 1 : 0,
        });

        setServiceInPast(serviceTimeHasPast(service?.endTime));
      }
    },
  });

  const [saveService] = useMutation<SaveServiceMutation, SaveServiceMutationVariables>(
    SAVE_SERVICE,
    {
      onCompleted: data => {
        if (data.saveService.errors.length === 0) {
          openAlert(AlertType.SUCCESS, t('service_saved'));
          router.push('/services');
        } else {
          openAlert(AlertType.ERROR, t('service_save_error'));
        }
      },
      refetchQueries: () =>
        id === 'new'
          ? [
              {
                query: SERVICES_CONNECTION,
                variables: { from, to },
              },
            ]
          : [
              {
                query: SERVICES_CONNECTION,
                variables: { from, to },
              },
              {
                query: LOAD_SERVICE,
                variables: { id: id.toString() },
              },
            ],
    }
  );

  const [deleteService] = useMutation<DeleteServiceMutation, DeleteServiceMutationVariables>(
    DELETE_SERVICE,
    {
      onCompleted: data => {
        if (data.deleteService.errors.length === 0) {
          openAlert(AlertType.SUCCESS, t('service_deleted'));
          Metrics.serviceDeleted(id.toString());
          router.push('/services');
        } else {
          openAlert(AlertType.ERROR, t('service_delete_error'));
        }
      },
      refetchQueries: [
        {
          query: SERVICES_CONNECTION,
          variables: { from, to },
        },
      ],
    }
  );

  const { data: onboardingData } = useQuery<
    CurrentOrganizationOnboardingQuery,
    CurrentOrganizationOnboardingQueryVariables
  >(CURRENT_ORGANIZATION_ONBOARDING);

  const addServiceTimeStep = onboardingData?.currentOrganization?.onboardingSteps?.find(
    step => step.key === OnboardingStepKeys.SERVICE
  );
  // If the step isn't present, assume it is complete
  const onboardingStepComplete = addServiceTimeStep?.completed ?? true;

  const [saveOnboardingStep] = useMutation<SaveOnboardingStepMutation>(SAVE_ONBOARDING_STEP, {
    refetchQueries: [
      {
        query: CURRENT_ORGANIZATION_ONBOARDING,
      },
    ],
  });

  const handleSaveService = (applyTo: ApplyEnum): void => {
    const serviceId =
      data && data.loadService && data.loadService.id ? data.loadService.id : uuid();
    if (state.date) {
      saveService({
        variables: {
          id: serviceId,
          input: getInput(serviceId, state, state.date),
          applyTo,
        },
      });
      if (!onboardingStepComplete) {
        saveOnboardingStep({
          variables: {
            input: {
              id: addServiceTimeStep?.id,
              completed: true,
            },
          },
        });
      }
    }
  };

  const handleDeleteService = (applyTo: ApplyEnum): void => {
    if (id) {
      deleteService({
        variables: {
          id: id.toString(),
          applyTo,
        },
      });
    }
  };

  const serviceTimeIsEditable = (): any => {
    if (id === 'new') {
      return true;
    } else {
      return data && data.loadService && serviceTimeIsUpcoming(data.loadService.scheduleTime);
    }
  };

  const onSubmit: FormEventHandler = (e): void => {
    e.preventDefault();

    if (serviceInPast) {
      return;
    }

    if (state.date && validTime(state.time) && serviceTimeIsEditable()) {
      if (
        id === 'new' ||
        (data && data.loadService && data.loadService.recurrence === RecurrenceEnum.ONCE)
      ) {
        handleSaveService(ApplyEnum.ALL);
      } else if (data && data.loadService && state.recurrence !== data.loadService.recurrence) {
        setState({
          ...state,
          modalOpen: true,
          modalType: ModalType.RECURRENCE_CHANGE,
        });
      } else if (
        data &&
        data.loadService &&
        (dayjs(data.loadService.scheduleTime).format('h:mma') !== state.time ||
          new Date(data.loadService.scheduleTime).getTime() !== state.date.getTime())
      ) {
        setState({
          ...state,
          modalOpen: true,
          modalType: ModalType.SAVE_REPEATING,
        });
      } else {
        handleSaveService(ApplyEnum.ALL);
      }
    } else {
      setState({
        ...state,
        dateValid: state.date !== undefined,
        timeValid: validTime(state.time),
      });

      if (data && data.loadService) {
        openAlert(AlertType.ERROR, t('content_not_saved'));
        setServiceInPast(serviceTimeHasPast(data.loadService.endTime));
      }
    }
  };

  const onDelete = (): void => {
    if (data && data.loadService && data.loadService.recurrence !== RecurrenceEnum.ONCE) {
      setState({
        ...state,
        modalOpen: true,
        modalType: ModalType.DELETE_REPEATING,
      });
    } else {
      handleDeleteService(ApplyEnum.CURRENT);
    }
  };

  const onChange = (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>): void => {
    if (e.currentTarget.name === 'time') {
      setState({
        ...state,
        [e.currentTarget.name]: e.currentTarget.value,
        timeValid: true,
      });
    } else if (
      e.currentTarget.name === 'recurrence' &&
      e.currentTarget.value === RecurrenceEnum.MONTHLY
    ) {
      setState({
        ...state,
        [e.currentTarget.name]: e.currentTarget.value,
        dayOfWeek: '',
      });
    } else if (
      e.currentTarget.name === 'recurrence' &&
      e.currentTarget.value === RecurrenceEnum.WEEKLY
    ) {
      setState({
        ...state,
        [e.currentTarget.name]: e.currentTarget.value,
        dayOfWeek: dayjs(state.date).locale('en').format('dd').toUpperCase(),
      });
    } else if (
      e.currentTarget.name === 'recurrence' &&
      e.currentTarget.value === RecurrenceEnum.ONCE
    ) {
      setState({
        ...state,
        [e.currentTarget.name]: e.currentTarget.value,
        endRepeat: undefined,
      });
    } else if (
      e.currentTarget.name === 'dayOfMonth' &&
      parseInt(e.currentTarget.value) > 4 &&
      state.recurrence === RecurrenceEnum.MONTHLY
    ) {
      setState({
        ...state,
        [e.currentTarget.name]: parseInt(e.currentTarget.value),
        dayOfWeek: '',
      });
    } else if (e.currentTarget.name === 'dayOfMonth') {
      setState({
        ...state,
        [e.currentTarget.name]: parseInt(e.currentTarget.value),
      });
    } else if (e.currentTarget.name === 'showEndRepeat' && e.currentTarget.value === '0') {
      setState({
        ...state,
        [e.currentTarget.name]: parseInt(e.currentTarget.value),
        endRepeat: undefined,
      });
    } else {
      setState({
        ...state,
        [e.currentTarget.name]: e.currentTarget.value,
      });
    }
  };

  const handleTimeChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const value = formatTime(e.currentTarget.value);
    if (validTime(value) || value === '') {
      setState({
        ...state,
        time: value,
      });
    } else {
      setState({
        ...state,
        time: value,
        timeValid: false,
      });
    }
  };

  const selectSelf = (event: any): void => event.target.select();

  const handleDayClick = (day: Date, { selected }: DayModifiers): void => {
    if (!DateUtils.isDayBefore(day, state.fromDate)) {
      setState({
        ...state,
        date: selected ? undefined : day,
        endRepeat: state.endRepeat && state.endRepeat < day ? day : state.endRepeat,
        dateValid: true,
        dayOfWeek: dayjs(day).locale('en').format('dd').toUpperCase(),
      });
    }
  };

  const handleEndRepeatDayClick = (day: Date, { selected }: DayModifiers): void => {
    if (!DateUtils.isDayBefore(day, state.date ? state.date : state.fromDate)) {
      setState({
        ...state,
        endRepeat: selected ? undefined : day,
        dateValid: true,
      });
    }
  };

  const closeModal = (): void => {
    setState({
      ...state,
      modalOpen: false,
      modalType: ModalType.DEFAULT,
    });
  };

  return (
    <>
      <AlertModal
        open={state.modalOpen}
        dismiss={closeModal}
        saveService={handleSaveService}
        deleteService={handleDeleteService}
        from={(data && data.loadService && recurrenceType(data.loadService.recurrence, t)) || ''}
        to={recurrenceType(state.recurrence, t) || ''}
        type={state.modalType}
      />
      <PageHeader
        title={t('common:service')}
        breadcrumb={t('common:services')}
        breadcrumbHref={'/services'}
      />
      <BackButtonStyleHelper href={'#'} onClick={() => maybeRouteTo('/services')}>
        <BackButton marginBlockEnd={6} blockNavigation />
      </BackButtonStyleHelper>
      {serviceInPast && (
        <Box marginBlockEnd={5}>
          <FormBanner type={FormBannerType.Information} withIcon customIcon={<ClosedLockIcon />}>
            {t('service_has_passed')}
          </FormBanner>
        </Box>
      )}
      <form onSubmit={onSubmit}>
        <Grid gridTemplateColumns='repeat(12, 1fr)' gridColumnGap='1rem' gridRowGap='1rem'>
          <GridItem gridColumn={['span 12', 'span 4', 'span 2']}>
            <Input
              name='time'
              label={t('time')}
              onChange={onChange}
              onBlur={handleTimeChange}
              onFocus={selectSelf}
              value={state.time}
              hint={state.timeValid ? '' : t('time_is_invalid')}
              error={!state.timeValid}
              disabled={serviceInPast}
              data-hj-whitelist
            />
            <TimeZone tz={Intl.DateTimeFormat().resolvedOptions().timeZone} />
          </GridItem>
          <GridItem gridColumn={['span 12', 'span 8', 'span 4']}>
            <SingleDatePicker
              name='date'
              label={t('date')}
              size={InputSize.MD}
              onDayClick={handleDayClick}
              selectedDay={state.date}
              fromMonth={state.fromDate}
              month={state.fromDate}
              hint={state.dateValid ? '' : t('date_must_be_selected')}
              error={!state.dateValid}
              disabled={serviceInPast}
              months={months}
              data-hj-whitelist
              locale={locale}
            />
          </GridItem>
          <GridItem gridColumn={['span 12', '1 / span 4', '1 / span 3']}>
            <Select
              name='recurrence'
              label={t('repeat')}
              onChange={onChange}
              value={state.recurrence}
              data-hj-whitelist
              disabled={serviceInPast}
            >
              <option value='ONCE'>{t('no')}</option>
              <option value='DAILY'>{t('daily')}</option>
              <option value='WEEKLY'>{t('weekly')}</option>
              <option value='MONTHLY'>{t('monthly')}</option>
              <option value='YEARLY'>{t('yearly')}</option>
            </Select>
          </GridItem>
          {state.recurrence === 'WEEKLY' && (
            <>
              <GridItem gridColumn={['span 12', 'span 2', 'span 1']} m={'30px auto 0'}>
                <OParagraph>{t('on')}</OParagraph>
              </GridItem>
              <DayOfWeekSelect
                name='dayOfWeek'
                onChange={onChange}
                value={state.dayOfWeek}
                disabled={true}
                data-hj-whitelist
              />
            </>
          )}
          {state.recurrence === 'MONTHLY' && (
            <>
              <GridItem gridColumn={['span 12', 'span 2', 'span 1']} m={'30px auto 0'}>
                <OParagraph>{t('on_the')}</OParagraph>
              </GridItem>
              <DayOfMonthSelect
                name='dayOfMonth'
                onChange={onChange}
                value={state.dayOfMonth}
                disabled={serviceInPast}
                data-hj-whitelist
              />
            </>
          )}
          {state.recurrence === 'MONTHLY' && state.dayOfMonth < 5 && state.dayOfMonth > 0 && (
            <DayOfWeekSelect
              name='dayOfWeek'
              onChange={onChange}
              value={state.dayOfWeek}
              day
              disabled={serviceInPast}
              data-hj-whitelist
            />
          )}
          {data && data.loadService && data.loadService.endRepeat && state.recurrence !== 'ONCE' && (
            <GridItem gridColumn={['span 12', '1 / span 4', '1 / span 2']}>
              <Select
                name='showEndRepeat'
                onChange={onChange}
                value={state.showEndRepeat}
                label='End Repeat'
                disabled={serviceInPast}
                data-hj-whitelist
              >
                <option value={0}>{t('never')}</option>
                <option value={1}>{t('on_date')}</option>
              </Select>
            </GridItem>
          )}
          {data &&
            data.loadService &&
            data.loadService.endRepeat &&
            state.showEndRepeat === 1 &&
            state.recurrence !== 'ONCE' && ( // eslint-disable-line eqeqeq
              <GridItem gridColumn={['span 12', 'span 8', 'span 4']} alignSelf={'end'}>
                <SingleDatePicker
                  name='endRepeat'
                  size={InputSize.MD}
                  onDayClick={handleEndRepeatDayClick}
                  selectedDay={state.endRepeat}
                  fromMonth={state.date}
                  month={state.date}
                  hint={state.dateValid ? '' : t('date_must_be_selected')}
                  error={!state.dateValid}
                  disabled={serviceInPast}
                  locale={locale}
                  months={months}
                  data-hj-whitelist
                />
              </GridItem>
            )}
          <GridItem gridColumn={['span 12', '1 / span 8', '1 / span 4']}>
            <PrimaryButton
              type='submit'
              marginBlockStart={5}
              marginInlineEnd={16}
              disabled={serviceInPast}
            >
              {t('common:save')}
            </PrimaryButton>
            {id !== 'new' && (
              <SecondaryRiskButton
                type='button'
                marginBlockStart={5}
                onClick={onDelete}
                disabled={serviceInPast}
              >
                {t('common:delete')}
              </SecondaryRiskButton>
            )}
          </GridItem>
          <GridItem gridColumn={['span 12']}>
            <Link href='/services'>
              <LinkButton marginBlockStart={3} buttonType={LinkButtonType.SECONDARY}>
                {t('common:cancel')}
              </LinkButton>
            </Link>
          </GridItem>
        </Grid>
      </form>
    </>
  );
};

export const getServerSideProps: GetServerSideProps = async ({ locale = DEFAULT_LOCALE }) => ({
  props: {
    ...(await serverSideTranslations(locale, ['common', 'service'])),
  },
});

export default Service;
